// react
import { cloneElement, useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';

// api
import {
  useGetBasicEnergyAccountQuery,
  useGetBasicEnergyDataQuery,
  usePutBasicEnergyManualRunMutation,
} from '@/api/energy-analysis/basicEnergySlice';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';

// shared components
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import AggregationSelector from '@/features/dashboards/components/AggregationSelector';
import LatestRefreshFooter from '@/features/dashboards/components/LatestRefreshFooter';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
import useSearchParams from '@/hooks/useStableSearchParams';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';

// relative
import ColumnChart from '../components/ColumnChart';
import DashboardDatagrid from '../components/DashboardDatagrid';
import DashboardDateRangeSelector from '../components/DashboardDateRangeSelector';
import StackedDataToggle from '../components/StackedDataToggle';
import { calculateMeterSummaryKpis } from '../utils/calculateMeterSummaryKpis';
import { formatAggregationBucketDate } from '../utils/formatAggregationBucketDate';
import type {
  BasicEnergyMeterSummary,
  BasicEnergyStackedUnit,
  BasicEnergyTableRow,
  UnitTag,
} from './basicEnergyTypes';

import { DateTime } from 'luxon';

const POLLING_INTERVAL_MS = 60 * 1000;

const WaterBasic = () => {
  const { t, i18n } = useTranslation('dashboards-water-basic');
  const { accountId } = useParams({ strict: false });
  const [searchParams] = useSearchParams();

  const startTimestamp = searchParams.get('from');
  const endTimestamp = searchParams.get('to');
  const agg = searchParams.get('agg') || 'day';
  const unitIds = searchParams.get('units');
  const theme = useTheme();
  const [putBasicEnergyManualRun] = usePutBasicEnergyManualRunMutation();
  const dispatch = useDispatch();
  const handleRefresh = async () => {
    const response = await putBasicEnergyManualRun({
      account_id: accountId,
      analysis_type: 'water',
    });
    if ('error' in response) {
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }
    dispatch(
      showSnackbar({
        message: t('messages.refreshStarted'),
        severity: 'success',
      }),
    );
  };

  const { data: basicEnergySubscription } = useGetBasicEnergyAccountQuery(
    { account_id: accountId },
    {
      skip: !accountId,
    },
  );

  const isUpdating = useMemo(
    () => Boolean(basicEnergySubscription?.WaterManualRunRequest),
    [basicEnergySubscription],
  );

  const {
    data: BasicEnergyData,
    isFetching,
    isLoading,
    isError,
  } = useGetBasicEnergyDataQuery(
    {
      analysisType: 'water',
      accountId,
      startTimestamp,
      endTimestamp,
      aggregateFrequency: agg,
      unitIds: unitIds && unitIds !== 'none' ? unitIds.split(',') : [],
    },
    {
      skip:
        !accountId || !startTimestamp || !endTimestamp || unitIds === 'none',
      pollingInterval: isUpdating ? POLLING_INTERVAL_MS : 0,
    },
  );

  useAlertProvider({
    enabled: isError,
    severity: 'error',
    message: t('messages.loadError'),
  });

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    {
      skip: !accountId,
    },
  );

  // table columns rely on translations, so define them inside the component
  const tableColumns: GridColDef<BasicEnergyTableRow>[] = [
    {
      field: 'name',
      headerName: t('table.columns.name'),
      flex: 1,
      renderCell: (params) => <Box>{params.value}</Box>,
    },
    {
      field: 'tags',
      headerName: t('table.columns.tags'),
      flex: 0.8,
      sortable: false,
      valueGetter: (value?: UnitTag[]) =>
        (value || []).map((tag) => tag.Text).join(', '),
      renderCell: (params) => {
        const tags = params.row.tags || [];
        return (
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ py: 0.5 }}
          >
            {tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag.Text}
                size="small"
                color="info"
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'sum',
      headerName: t('table.columns.sum'),
      type: 'number',
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatNumber(value),
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {formatNumber(params.value)}
        </Box>
      ),
    },
    {
      field: 'maxh',
      headerName: t('table.columns.maxh'),
      type: 'number',
      flex: 0.3,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => formatNumber(value),
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {formatNumber(params.value)}
        </Box>
      ),
    },
  ];

  const chartData = (() => {
    if (!BasicEnergyData || unitIds === 'none')
      return { categories: [], series: [] };

    const useStackedData = searchParams.get('stacked') === 'true';

    if (useStackedData && BasicEnergyData?.StackedData) {
      // Use StackedData for multi-series view
      // Each item in StackedData has: { UnitId, UnitName, DataType, DataPoints: [{ timestamp, sum, mean, min, max }] }
      const series = BasicEnergyData.StackedData.map(
        (unit: BasicEnergyStackedUnit) => ({
          name: unit.UnitName || `Unit ${unit.UnitId}`,
          data: unit.DataPoints.map((dataPoint) => [
            DateTime.fromISO(dataPoint.timestamp).toMillis(),
            dataPoint.sum || 0,
          ]).sort((a: number[], b: number[]) => a[0] - b[0]),
        }),
      );

      return { categories: [], series };
    }

    // Use AggregatedData for single series view
    if (!BasicEnergyData?.AggregatedData) return { categories: [], series: [] };

    const sorted = [...BasicEnergyData.AggregatedData].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp).toMillis() -
        DateTime.fromISO(b.timestamp).toMillis(),
    );
    const series = [
      {
        name: t('chart.series.consumption'),
        data: sorted.map((d) => [
          DateTime.fromISO(d.timestamp).toMillis(),
          d.sum,
        ]),
      },
    ];
    return { categories: [], series };
  })();

  const tableData =
    BasicEnergyData?.MeterSummaries?.map((ms: BasicEnergyMeterSummary) => {
      const unit = units?.find((u) => u.UnitId === ms.UnitId);
      return {
        id: ms.UnitId,
        tags: unit?.Tags || [],
        name: ms.UnitName,
        sum: ms.TotalValue,
        maxh: ms.MaxValue,
      };
    }) || [];

  const meterSummaryKpis = calculateMeterSummaryKpis(
    BasicEnergyData?.MeterSummaries,
    unitIds,
    BasicEnergyData?.AggregatedData,
  );

  // if (isError) {
  //   return (
  //     <ApiResponseAlert
  //       severity="error"
  //       title={t('messages.loadError')}
  //       object={error}
  //       goBack
  //     />
  //   );
  // }

  if (isLoading || !BasicEnergyData) {
    return (
      <>
        <PageHeader title={t('title')}>
          <DashboardDateRangeSelector />
        </PageHeader>
        <Stack spacing={2}>
          <Grid container spacing={2} alignItems="stretch">
            {[0, 1, 2, 3].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rounded" animation="wave" height={110} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" animation="wave" height={400} />
          <Skeleton variant="rounded" animation="wave" height={410} />
        </Stack>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('title')}>
        <DashboardDateRangeSelector />
      </PageHeader>
      <Stack spacing={2}>
        <Grid container spacing={2} alignItems="stretch">
          {[
            {
              titleKey: 'cards.totalConsumption',
              value: meterSummaryKpis.totalConsumption,
              icon: <WaterDropRoundedIcon />,
              iconColor: theme.palette.water.main,
              iconBgColor: theme.palette.water.main,
            },
            {
              titleKey: 'cards.averageConsumption',
              value: meterSummaryKpis.averageConsumption,
              period: t(`cards.aggregationPeriods.${agg}`),
              icon: <ShowChartRoundedIcon />,
              iconColor: theme.palette.info.main,
              iconBgColor: theme.palette.info.light,
            },
            {
              titleKey: 'cards.peakConsumption',
              value: meterSummaryKpis.peakConsumption,
              period: t(`cards.aggregationPeriods.${agg}`),
              icon: <ArrowUpwardRoundedIcon />,
              iconColor: theme.palette.error.main,
              iconBgColor: theme.palette.error.light,
              date: formatAggregationBucketDate(
                meterSummaryKpis.peakTimestamp,
                agg,
                i18n.resolvedLanguage || i18n.language,
              ),
              dateKey: `cards.peakDate.${agg}`,
            },
            {
              titleKey: 'cards.lowestConsumption',
              value: meterSummaryKpis.lowestConsumption,
              period: t(`cards.aggregationPeriods.${agg}`),
              icon: <ArrowDownwardRoundedIcon />,
              iconColor: theme.palette.success.main,
              iconBgColor: theme.palette.success.light,
              date: formatAggregationBucketDate(
                meterSummaryKpis.lowestTimestamp,
                agg,
                i18n.resolvedLanguage || i18n.language,
              ),
              dateKey: `cards.lowestDate.${agg}`,
            },
          ].map(
            ({
              titleKey,
              value,
              period,
              icon,
              iconColor,
              iconBgColor,
              date,
              dateKey,
            }) => (
              <Grid
                key={titleKey}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardHeader
                    title={period ? t(titleKey, { period }) : t(titleKey)}
                    titleTypographyProps={{ variant: 'h6' }}
                    avatar={
                      <Avatar
                        variant="rounded"
                        sx={{
                          bgcolor: alpha(iconBgColor, 0.1),
                          borderRadius: 3,
                        }}
                      >
                        {cloneElement(icon, {
                          sx: { verticalAlign: 'middle', color: iconColor },
                        })}
                      </Avatar>
                    }
                  />
                  <CardContent sx={{ pl: 9, pt: 0, mt: -2 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="text.primary"
                      >
                        {formatNumber(value)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {t('kpi.unit')}
                      </Typography>
                    </Stack>
                    {date && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {t(dateKey, { date })}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ),
          )}
        </Grid>
        <Card>
          <CardHeader
            title={t('cards.consumptionByDay', {
              period: t(`cards.aggregationPeriods.${agg}`),
            })}
            titleTypographyProps={{ variant: 'h6' }}
            action={
              <Stack direction="row" spacing={2} alignItems="center">
                <StackedDataToggle />
                <AggregationSelector />
              </Stack>
            }
          />
          <CardContent>
            <ColumnChart
              data={chartData}
              unit={t('kpi.unit')}
              color={theme.palette.water.main}
              height={300}
              isLoading={isFetching}
              isStacked={searchParams.get('stacked') === 'true'}
            />
          </CardContent>
        </Card>
        <DashboardDatagrid
          label={t('cards.powerUnits')}
          rows={tableData}
          columns={tableColumns}
          isLoading={isLoading}
        />
        <LatestRefreshFooter
          latestRefresh={BasicEnergyData?.LastCacheTimestamp}
          isUpdating={isUpdating}
          onRefresh={handleRefresh}
        />
      </Stack>
    </>
  );
};

export default WaterBasic;
